import { TransformWrapper, TransformComponent, ReactZoomPanPinchRef } from 'react-zoom-pan-pinch';
import { useEffect, useRef } from 'react';
import { DisplayItem } from '#/types/message';
import styles from './content-modal.module.css';

import TableDisplay from '../messages/content-carousel/table-display/table-display';
import ImageContainer from '../messages/content-carousel/image-container/image-container';

import { ContentModalMobile } from './content-modal-mobile';
import VideoContainer from '../messages/content-carousel/video-container/video-container';

interface ContentModalProps {
  isOpen: boolean;
  currentItem: DisplayItem | null;
  items: DisplayItem[];
  activeIndex: number;
  onClose: () => void;
  onNext: () => void;
  onPrevious: () => void;
}

function useBodyLock(isOpen: boolean) {
  useEffect(() => {
    if (!isOpen) return;

    const y = window.scrollY;
    const prevOverflow = document.body.style.overflow;
    const prevPosition = document.body.style.position;
    const prevTop = document.body.style.top;
    const prevWidth = document.body.style.width;
    const prevOverscroll = (document.body.style as any).overscrollBehavior;

    document.body.style.overflow = 'hidden';       // no scroll
    document.body.style.position = 'fixed';        // iOS: disable back-swipe scroll
    document.body.style.top = `-${y}px`;
    document.body.style.width = '100%';
    (document.body.style as any).overscrollBehavior = 'contain';

    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.position = prevPosition;
      document.body.style.top = prevTop;
      document.body.style.width = prevWidth;
      (document.body.style as any).overscrollBehavior = prevOverscroll;
      window.scrollTo(0, y);                       // restore scroll
    };
  }, [isOpen]);
}

export function ContentModal({
  isOpen,
  currentItem,
  items,
  activeIndex,
  onClose,
  onNext,
  onPrevious,
}: ContentModalProps) {


  const transformWrapperRef = useRef<ReactZoomPanPinchRef>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  useBodyLock(isOpen);

  useEffect(() => {
    if (!isOpen || !overlayRef.current) return;

    const el = overlayRef.current;
    const handler = (e: TouchEvent) => {
      // Prevent iOS edge back-swipe / overscroll rubber-banding
      e.preventDefault();
    };
    // passive:false is required to allow preventDefault
    el.addEventListener('touchmove', handler, { passive: false });
    return () => el.removeEventListener('touchmove', handler);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    // Push a sentinel state so we can absorb back/edge-swipe
    const sentinel = { modalOpen: true, ts: Date.now() };
    const url = new URL(window.location.href);
    // optional, helps some SPA routers: keep the same path but with a hash
    try { history.pushState(sentinel, '', `${url.pathname}${url.search}#modal`); } catch {}

    const handlePopState = () => {
      // While the modal is open, immediately re-push our sentinel
      // so back/edge-swipe doesn’t close the modal.
      if (isOpen) {
        try { history.pushState(sentinel, '', `${url.pathname}${url.search}#modal`); } catch {}
        return; // do NOT call onClose()
      }
    };

    window.addEventListener('popstate', handlePopState, { passive: true });

    return () => {
      window.removeEventListener('popstate', handlePopState);
      // Clean up the sentinel entry we added without navigating away
      if (history.state && history.state.modalOpen) {
        try { history.back(); } catch {}
      }
    };
  }, [isOpen]);

  const renderDesktop = () => {
    return (
      <>
        <div ref={overlayRef} className={styles.modal} onClick={onClose}>
          <div
            className={styles.modalContent}
          >
            <TransformWrapper
              ref={transformWrapperRef}
              initialScale={1.0}
              centerOnInit={true}
              minScale={0.4}
              maxScale={8}
              wheel={{ step: 0.2 }}
              doubleClick={{ disabled: false, step: 0.7 }}
            >

              {activeIndex > 0 && (
                <button
                  className={styles.navButton}
                  onClick={
                    (e) => {
                      e.stopPropagation();
                      onPrevious();
                    }
                  }
                  style={{ left: 0 }}
                  aria-label="Previous item"
                >
                  &#10094;
                </button>
              )}
              <TransformComponent
                wrapperClass={styles.transformWrapperStyles}
                wrapperStyle={{ width: '100%', height: '100%', cursor: 'pointer' }}
                contentClass={styles.transformContentStyles}
                contentStyle={{
                  backgroundColor: '#f0f0f0',
                  borderRadius: '0.2rem',
                  padding: '0.2rem',
                  cursor: 'grab'
                }}
              >
                <div onClick={(e) => e.stopPropagation()}>
                  {currentItem?.item_type === 'image' ? (
                    <ImageContainer
                      image={currentItem}
                      onImageClick={() => { }}
                      maxDisplayWidth={1000}
                      maxDisplayHeight={800}
                      internalActiveIndex={activeIndex}
                      forceDefaultLayout={false}
                    />
                  ) : currentItem?.item_type === 'table' ? (
                    <div className={styles.tableContentWrapper}>
                      <TableDisplay table={currentItem} />
                    </div>
                  ) : currentItem?.item_type === 'video' ? (
                    <VideoContainer
                      video={currentItem}
                      maxDisplayWidth={null}
                      maxDisplayHeight={800}
                      internalActiveIndex={activeIndex}
                    />
                  ) : (
                    <div className={styles.unsupportedContent}>
                      Content not supported in modal
                    </div>
                  )}
                </div>
              </TransformComponent>
              {activeIndex < items.length - 1 && (
                <button
                  className={styles.navButton}
                  onClick={
                    (e) => {
                      e.stopPropagation();
                      onNext();
                    }
                  }
                  style={{ right: 0 }}
                  aria-label="Next item"
                >
                  &#10095;
                </button>
              )}
            </TransformWrapper>
          </div>
        </div>
        <button className={styles.closeButton} onClick={onClose} aria-label="Close modal">×</button>
      </>
    )
  };

  if (!isOpen || !currentItem) return null;
  return (
    window.innerWidth <= 850 ?
      <ContentModalMobile
        isOpen={isOpen}
        currentItem={currentItem}
        items={items}
        activeIndex={activeIndex}
        onClose={onClose}
        onNext={onNext}
        onPrevious={onPrevious}
      /> : renderDesktop()
  );
}